import React, { useEffect, useRef } from 'react';
import DashboardAnnouncementBanner from './DashboardAnnouncementBanner';
import DashboardFieldMessageBanner from './DashboardFieldMessageBanner';

/** 约 3 条精小消息卡片可视高度（含间距），超出出现滚动条 */
const FIELD_STACK_MAX_HEIGHT = 210;

/**
 * 地图正上方居中堆叠（位于导航栏下方的内容区顶部）：
 * 系统公告 / 队员上线 / 前线上报
 */
export default function DashboardMapTopAlertStack({
  announcementVisible,
  announcement,
  onCloseAnnouncement,
  fieldMessageBanners = [],
  maxVisibleFieldBanners = 3,
  onCloseFieldMessageBanner,
  onFlyToFieldMessage,
  onlineNotifications = [],
  onOnlineNotificationClick
}) {
  const showAnnouncement = !!announcementVisible && !!announcement;
  const banners = Array.isArray(fieldMessageBanners) ? fieldMessageBanners : [];
  const online = Array.isArray(onlineNotifications) ? onlineNotifications : [];
  const showField = banners.length > 0;
  const showOnline = online.length > 0;
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current || banners.length <= maxVisibleFieldBanners) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [banners.length, maxVisibleFieldBanners]);

  if (!showAnnouncement && !showField && !showOnline) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 0,
        right: 0,
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        pointerEvents: 'none',
        padding: '0 12px'
      }}
    >
      <style>
        {`@keyframes zhxtMapTopSlideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .zhxt-field-banner-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(0,0,0,.28) transparent;
        }
        .zhxt-field-banner-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .zhxt-field-banner-scroll::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,.28);
          border-radius: 4px;
        }`}
      </style>
      <DashboardAnnouncementBanner
        visible={showAnnouncement}
        announcement={announcement}
        onClose={onCloseAnnouncement}
      />

      {showOnline && online.map((notification) => {
        const type = notification.type || 'online';
        const theme = type === 'offline'
          ? {
            bg: 'linear-gradient(135deg, #78909c, #607d8b)',
            border: '#546e7a',
            title: `⚪ ${notification.memberName} 离线`
          }
          : type === 'temp_offline'
            ? {
              bg: 'linear-gradient(135deg, #fb923c, #f59e0b)',
              border: '#ea580c',
              title: `🟠 ${notification.memberName} 暂时离线`
            }
            : type === 'identity_update'
              ? {
                bg: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                border: '#1d4ed8',
                title: `ℹ️ ${notification.memberName} 修改了设备信息`
              }
              : {
                bg: 'linear-gradient(135deg, #4caf50, #45a049)',
                border: '#45a049',
                title: `🟢 ${notification.memberName} 上线`
              };
        return (
          <div
            key={notification.id}
            role="button"
            tabIndex={0}
            title="点击定位到该队员"
            onClick={() => onOnlineNotificationClick?.(notification)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOnlineNotificationClick?.(notification);
              }
            }}
            style={{
              width: '100%',
              maxWidth: 330,
              pointerEvents: 'auto',
              background: theme.bg,
              color: '#fff',
              padding: '7px 12px',
              borderRadius: 6,
              fontSize: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              animation: 'zhxtMapTopSlideDown 0.28s ease-out',
              border: `1px solid ${theme.border}`,
              cursor: onOnlineNotificationClick ? 'pointer' : 'default',
              textAlign: 'left'
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {theme.title}
            </div>
            <div style={{ fontSize: 11, opacity: 0.92 }}>
              {notification.detail
                || `${notification.orgName} - ${notification.groupName}`}
            </div>
            <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>
              {notification.timestamp}
              {onOnlineNotificationClick ? ' · 点击定位' : ''}
            </div>
          </div>
        );
      })}

      {showField && (
        <div
          ref={scrollRef}
          className="zhxt-field-banner-scroll"
          style={{
            width: '100%',
            maxWidth: 330,
            maxHeight: FIELD_STACK_MAX_HEIGHT,
            overflowY: banners.length > maxVisibleFieldBanners ? 'auto' : 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
            pointerEvents: 'auto',
            paddingRight: banners.length > maxVisibleFieldBanners ? 2 : 0
          }}
        >
          {banners.map((msg) => (
            <DashboardFieldMessageBanner
              key={msg.bannerId || msg.id}
              visible
              message={msg}
              onClose={() => onCloseFieldMessageBanner?.(msg.bannerId)}
              onFlyTo={onFlyToFieldMessage}
            />
          ))}
        </div>
      )}
    </div>
  );
}
